import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  PLATFORM_ID,
  Inject,
  OnDestroy,
  ChangeDetectorRef,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  CarIcon,
  ChevronRightIcon,
  LucideAngularModule,
  MapPinIcon,
} from 'lucide-angular';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MiGarage } from '../../garage/mi-garage/mi-garage';
import { MyStoreSelector } from '../../sucursales/my-store-selector';
import { isPlatformBrowser } from '@angular/common';
import { StoreService } from '../../../services/store.service';
import { Subscription } from 'rxjs';
import { Sucursal } from '../../../types/stores';
import { Auto } from '../../../types/vehicle.types';
import { VehicleService } from '../../../services/vehicle.service';

type WhichShow = 'vehicle' | 'store' | 'both';

@Component({
  selector: 'allas-garage-dropdown',

  imports: [
    CommonModule,
    LucideAngularModule,
    DynamicDialogModule,
    MyStoreSelector,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'garage-dropdown.html',
  providers: [DialogService],
})
export class GarageDropdownComponent implements OnDestroy {
  readonly carIcon = CarIcon;
  readonly mapPinIcon = MapPinIcon;
  readonly chevronRightIcon = ChevronRightIcon;

  @Input() whichShow: WhichShow = 'both';

  isDesktop: boolean = true;
  selectedVehicle: Auto | undefined;
  selectedStore: Sucursal | null = null;
  private vehiclesSubscription: Subscription | undefined;
  private storeSubscription: Subscription | undefined;
  isStoreOpen: boolean = false;

  thereIsStoreSelected = signal(false);
  storeData = signal<{ storeName: string; statusText: string }>({
    storeName: 'Mi Tienda',
    statusText: '',
  });

  constructor(
    private router: Router,
    private dialogService: DialogService,
    private vehiclesService: VehicleService,
    private storeService: StoreService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.subscribeToData();
  }

  subscribeToData(): void {
    this.selectedVehicle = this.vehiclesService.getDefaultVehicle();
    this.selectedStore = this.storeService.getSelectedStore();

    this.subscribeToVehicles();
    this.subscribeToStore();
    this.verifyIsDesktop();
    this.determinateIsOpen();

    // Actualizar datos de la tienda al inicializar
    this.updateStoreData(this.selectedStore);
  }

  ngOnDestroy(): void {
    if (this.vehiclesSubscription) {
      this.vehiclesSubscription.unsubscribe();
    }
    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }
  }

  verifyIsDesktop(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isDesktop = window.innerWidth > 1024;
    }
  }

  private subscribeToVehicles(): void {
    this.vehiclesSubscription = this.vehiclesService.vehicles$.subscribe(() => {
      this.refreshSelectedVehicle();
    });
  }

  private subscribeToStore(): void {
    this.storeSubscription = this.storeService.selectedStore$.subscribe(
      (selectedStore) => {
        this.selectedStore = selectedStore;
        this.updateStoreData(selectedStore);
      }
    );
  }

  private updateStoreData(store: Sucursal | null) {
    setTimeout(() => {
      this.thereIsStoreSelected.set(!!store);
      this.storeData.set({
        storeName: store?.name || 'Mi Tienda',
        statusText: store ? (this.isStoreOpen ? 'Abierto' : 'Cerrado') : '',
      });
    }, 0);
  }

  goToGarage(): void {
    // this.router.navigate(['/garage']);
    // return;

    // DOC: Ahora esto nos lleva al /garage, ya que según el prototipo, ya no será un modal, sino una página completa
    if (this.isDesktop) {
      this.dialogService.open(MiGarage, {
        header: 'Mi Garage',
        width: '70vw',
        modal: true,
        draggable: false,
        resizable: false,
        closable: true,
        showHeader: true,
        closeOnEscape: true,
        data: {
          showHeader: false,
        },
      });
    } else {
      this.router.navigate(['/garage']);
    }
  }

  determinateIsOpen(): boolean {
    const nowAdjusted = new Date(new Date().getTime() - 6 * 60 * 60 * 1000);

    const hour = nowAdjusted.getUTCHours();
    const day = nowAdjusted.getUTCDay();

    const isSunday = day === 0;
    const isSaturday = day === 6;

    // Si es domingo, está cerrado a la verga
    if (isSunday) {
      this.isStoreOpen = false;
      return false;
    }

    // Si es sábado y ya pasó las 3PM, está cerrado a la verga
    if (isSaturday && hour > 15) {
      this.isStoreOpen = false;
      return false;
    }

    // De lo contrario, está abierto
    this.isStoreOpen = true;
    return true;
  }

  getVehicleDisplayText(): string {
    if (this.selectedVehicle) {
      const displayText = `${this.selectedVehicle.anio} ${this.selectedVehicle.marca} ${this.selectedVehicle.modelo}`;
      return displayText;
    }
    return 'Mi Auto';
  }

  refreshSelectedVehicle(): void {
    setTimeout(() => {
      this.selectedVehicle = this.vehiclesService.getDefaultVehicle();
      this.cdr.detectChanges();
    }, 100);
  }

  getStoreDisplayText(): { storeName: string; statusText: string } {
    if (this.selectedStore) {
      return {
        storeName: this.selectedStore.name,
        statusText: this.isStoreOpen ? 'Abierto' : 'Cerrado',
      };
    }
    return {
      storeName: 'Mi Tienda',
      statusText: '',
    };
  }

  get showVehicle(): boolean {
    return this.whichShow === 'vehicle' || this.whichShow === 'both';
  }

  get showStore(): boolean {
    return this.whichShow === 'store' || this.whichShow === 'both';
  }

  get showSeparator(): boolean {
    return this.whichShow === 'both';
  }

  get vehicleWidth(): string {
    return this.whichShow === 'both' ? 'w-1/2' : 'w-full';
  }

  get storeWidth(): string {
    return this.whichShow === 'both' ? 'w-1/2' : 'w-full';
  }

  handleStoreSelect(store: Sucursal): void {
    this.storeService.updateStore(store);
  }
}
